import { useMemo, useRef, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import type { PreviewTake, StageStateV1, Vector3 } from '@shotstead/contracts'
import { validateStageStateV1 } from '@shotstead/contracts'
import { DEMO_STAGE_STATE } from '@shotstead/contracts/demo'
import {
  InMemoryStageRepository,
  SHOTSTEAD_STAGE_HISTORY_STORAGE_KEY,
  cloneStageState,
  deserializeStageHistory,
  diffStageStates,
  patchCameraState,
  sameStageContinuityState,
  serializeStageHistory,
} from '@shotstead/core-candidate'
import { StageScene, rehearsalDurationSeconds } from '@shotstead/stage-runtime-three'
import { JudgeGuide } from './JudgeGuide'
import { TopDownStage } from './TopDownStage'
import { capturePreviewTakeFromCanvas } from './runtime/capturePreview'
import { useRehearsalClock } from './runtime/useRehearsalClock'

type LabeledTake = {
  label: 'A' | 'B'
  objectUrl: string
  take: PreviewTake
}

function loadInitialHistory(): StageStateV1[] {
  const saved = window.localStorage.getItem(SHOTSTEAD_STAGE_HISTORY_STORAGE_KEY)
  if (!saved) return [cloneStageState(DEMO_STAGE_STATE)]

  try {
    return deserializeStageHistory(saved)
  } catch {
    return [cloneStageState(DEMO_STAGE_STATE)]
  }
}

function NumberField({
  label,
  value,
  step = 0.1,
  onChange,
}: {
  label: string
  value: number
  step?: number
  onChange: (value: number) => void
}) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <input
        type="number"
        step={step}
        value={Number(value.toFixed(3))}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  )
}

function CameraControls({
  stage,
  onPosition,
  onFov,
}: {
  stage: StageStateV1
  onPosition: (axis: keyof Vector3, value: number) => void
  onFov: (value: number) => void
}) {
  const position = stage.camera.transform.position

  return (
    <section className="panel control-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Camera blocking</span>
          <h2>Capture camera</h2>
        </div>
        <span className="pill">{stage.camera.camera_id}</span>
      </div>
      <div className="field-grid">
        <NumberField label="X" value={position.x} onChange={(value) => onPosition('x', value)} />
        <NumberField label="Y" value={position.y} onChange={(value) => onPosition('y', value)} />
        <NumberField label="Z" value={position.z} onChange={(value) => onPosition('z', value)} />
        <NumberField label="FOV" value={stage.camera.fov_degrees} step={1} onChange={onFov} />
      </div>
      <p className="muted">
        Move only the camera. The performer, prop, motion and room remain anchored to the same Stage.
      </p>
    </section>
  )
}

function VersionPanel({
  committed,
  working,
  history,
  takeA,
  takeB,
  onCommitCamera,
}: {
  committed: StageStateV1
  working: StageStateV1
  history: StageStateV1[]
  takeA: LabeledTake | null
  takeB: LabeledTake | null
  onCommitCamera: () => void
}) {
  const workingDiff = diffStageStates(committed, working)
  const hasSemanticChange = workingDiff.changed_paths.some(
    (path) => path !== 'stage_version' && path !== 'parent_stage_version',
  )
  const canCommitCamera =
    committed.stage_version === 1 && Boolean(takeA) && hasSemanticChange && workingDiff.camera_only

  const v1 = history.find((stage) => stage.stage_version === 1)
  const latestDiff = v1 && committed.stage_version > 1 ? diffStageStates(v1, committed) : null
  const continuity = v1 && committed.stage_version > 1 ? sameStageContinuityState(v1, committed) : null

  return (
    <section className="panel version-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Stage lineage</span>
          <h2>Stage versions</h2>
        </div>
        <span className="pill">committed v{committed.stage_version}</span>
      </div>

      <div className="version-chain" data-testid="version-chain">
        {history.map((stage, index) => (
          <span key={stage.stage_version} className="version-node">
            v{stage.stage_version}
            {index > 0 ? <small>← v{stage.parent_stage_version}</small> : <small>root</small>}
          </span>
        ))}
      </div>

      <p className="muted">
        Every Take remains tied to the committed Stage version that produced it. Working draft: {hasSemanticChange ? `${workingDiff.changed_paths.length} changed paths` : 'clean'}.
      </p>

      {hasSemanticChange ? (
        <div className={`diff-status ${workingDiff.camera_only ? 'good' : 'warn'}`} data-testid="working-diff-status">
          {workingDiff.camera_only ? 'Camera-only draft' : 'Draft changes more than the camera'}
        </div>
      ) : null}

      <button type="button" className="primary" disabled={!canCommitCamera} onClick={onCommitCamera}>
        Commit Camera Setup → v2
      </button>
      {!takeA && committed.stage_version === 1 ? (
        <p className="muted">Capture Take A from committed v1 before creating the camera-only child version.</p>
      ) : null}

      {latestDiff ? (
        <div className="continuity-proof" data-testid="continuity-proof">
          <strong>{latestDiff.camera_only && continuity ? 'Same-stage continuity: PASS' : 'Continuity proof incomplete'}</strong>
          <span>v1 → v{committed.stage_version}</span>
          <span>camera-only diff: {latestDiff.camera_only ? 'yes' : 'no'}</span>
          <span>environment / performer / prop / motion unchanged: {continuity ? 'yes' : 'no'}</span>
          <code>{latestDiff.changed_paths.join(', ')}</code>
        </div>
      ) : null}

      {takeA || takeB ? (
        <div className="take-lineage-mini">
          {takeA ? <span>Take A → v{takeA.take.stage_version}</span> : null}
          {takeB ? <span>Take B → v{takeB.take.stage_version}</span> : null}
        </div>
      ) : null}
    </section>
  )
}

function TakeCard({ item }: { item: LabeledTake }) {
  return (
    <article className="take-card" data-testid={`take-${item.label.toLowerCase()}`}>
      <div className="take-heading">
        <strong>Take {item.label}</strong>
        <span className="pill">Stage v{item.take.stage_version}</span>
      </div>
      <video src={item.objectUrl} controls playsInline />
      <dl>
        <div><dt>Take</dt><dd>{item.take.take_id}</dd></div>
        <div><dt>Stage</dt><dd>{item.take.stage_id} v{item.take.stage_version}</dd></div>
        <div><dt>Camera</dt><dd>{item.take.camera_id}</dd></div>
        <div><dt>Runtime</dt><dd>{item.take.runtime_adapter}@{item.take.runtime_version}</dd></div>
        <div><dt>MIME</dt><dd>{item.take.mime_type}</dd></div>
      </dl>
    </article>
  )
}

export function App() {
  const initialHistoryRef = useRef<StageStateV1[] | null>(null)
  if (!initialHistoryRef.current) initialHistoryRef.current = loadInitialHistory()

  const repositoryRef = useRef<InMemoryStageRepository | null>(null)
  if (!repositoryRef.current) {
    repositoryRef.current = new InMemoryStageRepository(initialHistoryRef.current)
  }

  const repository = repositoryRef.current
  const [history, setHistory] = useState<StageStateV1[]>(() => repository.listVersions())
  const [committedStage, setCommittedStage] = useState<StageStateV1>(() => repository.getLatest())
  const [workingStage, setWorkingStage] = useState<StageStateV1>(() => repository.getLatest())
  const [persistenceMessage, setPersistenceMessage] = useState('Committed history loaded')
  const [captureMessage, setCaptureMessage] = useState('Ready')
  const [takeA, setTakeA] = useState<LabeledTake | null>(null)
  const [takeB, setTakeB] = useState<LabeledTake | null>(null)
  const [viewfinderReady, setViewfinderReady] = useState(false)
  const viewfinderCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const durationSeconds = useMemo(() => rehearsalDurationSeconds(workingStage), [workingStage])
  const clock = useRehearsalClock(durationSeconds)
  const workingDiff = useMemo(
    () => diffStageStates(committedStage, workingStage),
    [committedStage, workingStage],
  )
  const workingDirty = workingDiff.changed_paths.some(
    (path) => path !== 'stage_version' && path !== 'parent_stage_version',
  )

  const patchPosition = (axis: keyof Vector3, value: number) => {
    setWorkingStage((current) =>
      patchCameraState(current, {
        transform: {
          position: {
            ...current.camera.transform.position,
            [axis]: value,
          },
        },
      }),
    )
  }

  const patchCameraXZ = (x: number, z: number) => {
    setWorkingStage((current) =>
      patchCameraState(current, {
        transform: {
          position: {
            ...current.camera.transform.position,
            x,
            z,
          },
        },
      }),
    )
  }

  const patchFov = (value: number) => {
    const bounded = Math.min(120, Math.max(10, value))
    setWorkingStage((current) => patchCameraState(current, { fov_degrees: bounded }))
  }

  const persistHistory = (nextHistory = repository.listVersions()) => {
    window.localStorage.setItem(SHOTSTEAD_STAGE_HISTORY_STORAGE_KEY, serializeStageHistory(nextHistory))
    setPersistenceMessage(`Saved ${nextHistory.length} committed Stage version${nextHistory.length === 1 ? '' : 's'}`)
  }

  const saveAndReloadPage = () => {
    if (workingDirty) {
      setPersistenceMessage('Commit or reset the working draft before reloading committed history.')
      return
    }
    persistHistory()
    window.location.reload()
  }

  const restoreLatestCommitted = () => {
    const latest = repository.getLatest()
    clock.reset()
    setCommittedStage(latest)
    setWorkingStage(latest)
    setPersistenceMessage(`Restored committed Stage v${latest.stage_version}`)
  }

  const resetFixture = () => {
    window.localStorage.removeItem(SHOTSTEAD_STAGE_HISTORY_STORAGE_KEY)
    clock.reset()
    takeA && URL.revokeObjectURL(takeA.objectUrl)
    takeB && URL.revokeObjectURL(takeB.objectUrl)
    setTakeA(null)
    setTakeB(null)

    const freshRepository = new InMemoryStageRepository([cloneStageState(DEMO_STAGE_STATE)])
    repositoryRef.current = freshRepository
    const latest = freshRepository.getLatest()
    setHistory(freshRepository.listVersions())
    setCommittedStage(latest)
    setWorkingStage(latest)
    setPersistenceMessage('Reset to committed Stage v1 fixture')
    window.location.reload()
  }

  const commitCameraSetup = () => {
    const diff = diffStageStates(committedStage, workingStage)
    if (!diff.camera_only || !sameStageContinuityState(committedStage, workingStage)) {
      setPersistenceMessage('Commit rejected: camera-only continuity requires a camera state change.')
      return
    }

    try {
      const committed = repository.commitFromBase(committedStage.stage_version, workingStage)
      const nextHistory = repository.listVersions()
      setHistory(nextHistory)
      setCommittedStage(committed)
      setWorkingStage(committed)
      persistHistory(nextHistory)
      setPersistenceMessage(`Committed Stage v${committed.stage_version} from parent v${committed.parent_stage_version}`)
    } catch (error) {
      setPersistenceMessage(error instanceof Error ? error.message : 'Stage commit failed')
    }
  }

  const captureCommittedTake = async (label: 'A' | 'B') => {
    const canvas = viewfinderCanvasRef.current
    if (!canvas || !viewfinderReady) {
      setCaptureMessage('Viewfinder canvas is not ready')
      return
    }
    if (workingDirty) {
      setCaptureMessage('Capture rejected: commit or reset the working draft first.')
      return
    }
    if (label === 'A' && committedStage.stage_version !== 1) {
      setCaptureMessage('Take A must be captured from committed Stage v1.')
      return
    }
    if (label === 'B' && committedStage.stage_version !== 2) {
      setCaptureMessage('Take B must be captured from committed Stage v2.')
      return
    }

    setCaptureMessage(`Recording Take ${label} from Stage v${committedStage.stage_version}…`)
    clock.replay()

    try {
      const result = await capturePreviewTakeFromCanvas({
        canvas,
        stage: committedStage,
        durationSeconds,
      })
      const item: LabeledTake = { label, objectUrl: result.objectUrl, take: result.take }
      if (label === 'A') {
        if (takeA) URL.revokeObjectURL(takeA.objectUrl)
        setTakeA(item)
      } else {
        if (takeB) URL.revokeObjectURL(takeB.objectUrl)
        setTakeB(item)
      }
      setCaptureMessage(`Captured Take ${label} from Stage v${result.take.stage_version}: ${result.blob.size.toLocaleString()} bytes`)
    } catch (error) {
      clock.stop()
      setCaptureMessage(error instanceof Error ? error.message : `Take ${label} capture failed`)
    }
  }

  const validation = validateStageStateV1(workingStage)
  const takeSequenceComplete = Boolean(takeA && takeB)
  const demoStep: 1 | 2 | 3 | 4 = !takeA
    ? 1
    : committedStage.stage_version === 1
      ? 2
      : !takeB
        ? 3
        : 4

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <span className="eyebrow">Shotstead - persistent AI virtual production stage</span>
          <h1>Same stage. New camera. Proven lineage.</h1>
          <p className="hero-deck">
            Block once. Shoot new angles without rebuilding the world. Every Take stays tied to the exact Stage version that produced it.
          </p>
        </div>
        <div className="status-stack">
          <span className={`status-dot ${validation.valid ? 'ok' : 'bad'}`}>
            {validation.valid ? 'Stage schema valid' : 'Stage schema invalid'}
          </span>
          <span className="muted">Stage {workingStage.stage_id} · committed v{committedStage.stage_version}</span>
        </div>
      </header>

      <JudgeGuide step={demoStep} />

      <section className="as1-view-grid">
        <article className="viewport-card topdown-card">
          <div className="viewport-label">
            <span>2D Stage</span>
            <small>Drag the camera · same world</small>
          </div>
          <div className="topdown-wrap">
            <TopDownStage stage={workingStage} onCameraPosition={patchCameraXZ} />
          </div>
        </article>

        <article className="viewport-card director-card">
          <div className="viewport-label">
            <span>3D Director View</span>
            <small>See the whole blocking</small>
          </div>
          <div className="canvas-wrap">
            <Canvas shadows dpr={[1, 1.5]}>
              <StageScene stage={workingStage} elapsedSeconds={clock.elapsedSeconds} directorView />
            </Canvas>
          </div>
        </article>

        <article className="viewport-card viewfinder-card">
          <div className="viewport-label">
            <span>Viewfinder</span>
            <small>What the camera sees</small>
          </div>
          <div className="canvas-wrap viewfinder-wrap">
            <Canvas
              shadows
              dpr={[1, 1.5]}
              gl={{ preserveDrawingBuffer: true, antialias: true }}
              onCreated={({ gl }) => {
                viewfinderCanvasRef.current = gl.domElement
                setViewfinderReady(true)
              }}
            >
              <StageScene stage={workingStage} elapsedSeconds={clock.elapsedSeconds} />
            </Canvas>
            <div className="frame-lines" aria-hidden="true" />
          </div>
        </article>
      </section>

      <section className="lower-grid as1-controls-grid">
        <CameraControls stage={workingStage} onPosition={patchPosition} onFov={patchFov} />

        <VersionPanel
          committed={committedStage}
          working={workingStage}
          history={history}
          takeA={takeA}
          takeB={takeB}
          onCommitCamera={commitCameraSetup}
        />

        <section className="panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Preview performance</span>
              <h2>Runtime clock</h2>
            </div>
            <span className="timecode">{clock.elapsedSeconds.toFixed(2)} / {durationSeconds.toFixed(2)}s</span>
          </div>
          <div className="button-row">
            <button type="button" onClick={clock.start} disabled={clock.isPlaying}>Start</button>
            <button type="button" onClick={clock.stop} disabled={!clock.isPlaying}>Stop</button>
            <button type="button" onClick={clock.reset}>Reset</button>
            <button type="button" onClick={clock.replay}>Replay</button>
          </div>
          <div className="progress-track">
            <div
              className="progress-value"
              style={{ width: `${Math.min(100, (clock.elapsedSeconds / durationSeconds) * 100)}%` }}
            />
          </div>
          <p className="muted">Replay the same blocking before recording a Take.</p>
        </section>

        <section className="panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Return to the same world</span>
              <h2>Persistence</h2>
            </div>
          </div>
          <div className="button-row wrap">
            <button type="button" onClick={() => persistHistory()}>Save history</button>
            <button type="button" onClick={restoreLatestCommitted}>Restore latest</button>
            <button type="button" onClick={saveAndReloadPage}>Save + page reload</button>
            <button type="button" className="ghost" onClick={resetFixture}>Reset fixture</button>
          </div>
          <p className="muted">{persistenceMessage}</p>
        </section>
      </section>

      <section className="panel take-workflow-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Continuity proof</span>
            <h2>Take A / Take B</h2>
          </div>
          <span className={`status-dot ${takeSequenceComplete ? 'ok' : ''}`}>
            {takeSequenceComplete ? 'Two-take lineage complete' : 'Capture v1, change camera, commit v2, capture again'}
          </span>
        </div>

        <div className="button-row wrap take-actions">
          <button
            type="button"
            className="primary compact"
            disabled={!viewfinderReady || workingDirty || committedStage.stage_version !== 1}
            onClick={() => captureCommittedTake('A')}
          >
            Capture Take A (v1)
          </button>
          <button
            type="button"
            className="primary compact"
            disabled={!viewfinderReady || workingDirty || committedStage.stage_version !== 2}
            onClick={() => captureCommittedTake('B')}
          >
            Capture Take B (v2)
          </button>
          <span className="muted capture-message" data-testid="capture-message">{captureMessage}</span>
        </div>

        <div className="take-grid">
          {takeA ? <TakeCard item={takeA} /> : <div className="take-placeholder">Take A awaits committed Stage v1.</div>}
          {takeB ? <TakeCard item={takeB} /> : <div className="take-placeholder">Take B awaits camera-only committed Stage v2.</div>}
        </div>
      </section>

      <footer className="debug-strip">
        <span>stage: {workingStage.stage_id}</span>
        <span>committed: v{committedStage.stage_version}</span>
        <span>working: {workingDirty ? 'dirty' : 'clean'}</span>
        <span>performer: {workingStage.performers[0]?.performer_id ?? 'none'}</span>
        <span>prop: {workingStage.props[0]?.prop_id ?? 'none'}</span>
        <span>camera: {workingStage.camera.camera_id}</span>
        <span>capture: browser MediaRecorder</span>
      </footer>
    </main>
  )
}
